import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import DriftStatusBadge from './DriftStatusBadge';
import './DriftSummaryCard.css';

/**
 * DriftSummaryCard
 *
 * PRD-67 §4.4 pivot: primary hero now reads PRD-46-authoritative alarm-severity
 * counts from `summary.driftSummary` (OPEN alarms). Legacy asset-state coverage
 * (stable / changed / alert from `summary.driftCoverage`) renders as a smaller
 * secondary strip below the hero.
 *
 * PRD-67 §4.3: removed the prior `return null` when total === 0 — empty states
 * now render explicit copy keyed off `meta.driftSummary`.
 */
function DriftSummaryCard({ assets = [], driftSummary, driftCoverage, meta }) {
    // Alarm-severity hero counts (PRD-46 authoritative).
    const severityCounts = driftSummary || { critical: 0, high: 0, medium: 0, low: 0, total: 0 };
    const totalOpenAlarms = severityCounts.total || 0;

    // Asset-state coverage strip (legacy field, secondary). Prefer backend-provided
    // distribution; fall back to deriving from assets[] for older payloads.
    const coverage = useMemo(() => {
        if (driftCoverage && typeof driftCoverage === 'object') {
            return {
                stable: driftCoverage.stable || 0,
                changed: driftCoverage.changed || 0,
                alert: driftCoverage.alert || 0,
                total: driftCoverage.total || 0
            };
        }
        // Fallback: derive from assets[] (preserves existing behavior on older API).
        const derived = { stable: 0, changed: 0, alert: 0, total: 0 };
        for (const asset of assets) {
            const status = asset.driftStatus;
            if (status === 'stable' || status === 'changed' || status === 'alert') {
                derived[status]++;
                derived.total++;
            }
        }
        return derived;
    }, [driftCoverage, assets]);

    // Recent changes list — derived from assets[] (still the best source for per-asset detail).
    const recentChanges = useMemo(() => {
        const list = [];
        for (const asset of assets) {
            const status = asset.driftStatus;
            if (status === 'changed' || status === 'alert') {
                if (asset.driftSummary) {
                    list.push({
                        assetId: asset.assetId || asset.rowKey,
                        assetValue: asset.value,
                        status,
                        summary: asset.driftSummary,
                        evaluatedAt: asset.lastDriftEvalAt
                    });
                }
            }
        }
        list.sort((a, b) => {
            if (!a.evaluatedAt) return 1;
            if (!b.evaluatedAt) return -1;
            return new Date(b.evaluatedAt) - new Date(a.evaluatedAt);
        });
        return list.slice(0, 5);
    }, [assets]);

    // PRD-67 §4.3: empty-state copy keyed off meta. No "awaiting window" class
    // for Drift Summary — alarms are time-independent.
    const emptyState = useMemo(() => {
        if (totalOpenAlarms > 0) return null;
        if (!meta) {
            return {
                primary: 'No open drift alarms.',
                hint: null,
                cta: null
            };
        }
        const assetsScanned = meta.assetsScanned ?? 0;
        if (assetsScanned === 0) {
            return {
                primary: 'Run your first scan to see drift activity.',
                hint: null,
                cta: <Link to="/assets" className="drift-summary-empty__cta">Go to Asset Management</Link>
            };
        }
        return {
            primary: `${assetsScanned} asset${assetsScanned === 1 ? '' : 's'} scanned — no open drift alarms.`,
            hint: 'Drift alarms fire when your attack surface changes (new ports, expired certs, DNS changes, etc.).',
            cta: null
        };
    }, [totalOpenAlarms, meta]);

    const severities = [
        { key: 'critical', label: 'Critical' },
        { key: 'high', label: 'High' },
        { key: 'medium', label: 'Medium' },
        { key: 'low', label: 'Low' }
    ];

    return (
        <div className="drift-summary-card">
            {/* Hero: alarm severity counts (PRD-46 authoritative) */}
            {totalOpenAlarms > 0 ? (
                <div className="drift-summary-severity">
                    {severities.map(sev => (
                        <div
                            key={sev.key}
                            className={`drift-summary-severity__item drift-summary-severity__item--${sev.key}`}
                        >
                            <span className="drift-summary-severity__value">
                                {severityCounts[sev.key] || 0}
                            </span>
                            <span className="drift-summary-severity__label">{sev.label}</span>
                        </div>
                    ))}
                </div>
            ) : (
                emptyState && (
                    <div className="drift-summary-empty">
                        <p className="drift-summary-empty__primary">{emptyState.primary}</p>
                        {emptyState.hint && (
                            <p className="drift-summary-empty__hint">{emptyState.hint}</p>
                        )}
                        {emptyState.cta}
                    </div>
                )
            )}

            {/* Secondary: Drift Coverage strip (legacy asset-state) */}
            {coverage.total > 0 && (
                <div className="drift-summary-coverage">
                    <h5 className="drift-summary-coverage__title">Drift Coverage</h5>
                    <div className="drift-summary-coverage__stats">
                        <div className="drift-summary-coverage__stat drift-summary-coverage__stat--stable">
                            <span className="drift-summary-coverage__value">{coverage.stable}</span>
                            <span className="drift-summary-coverage__label">Stable</span>
                        </div>
                        <div className="drift-summary-coverage__stat drift-summary-coverage__stat--changed">
                            <span className="drift-summary-coverage__value">{coverage.changed}</span>
                            <span className="drift-summary-coverage__label">Changed</span>
                        </div>
                        <div className="drift-summary-coverage__stat drift-summary-coverage__stat--alert">
                            <span className="drift-summary-coverage__value">{coverage.alert}</span>
                            <span className="drift-summary-coverage__label">Alert</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Recent Changes (still derived from per-asset detail) */}
            {recentChanges.length > 0 && (
                <div className="drift-summary-changes">
                    <h4 className="drift-summary-changes__title">Recent Changes</h4>
                    <div className="drift-summary-changes__list">
                        {recentChanges.map((change, i) => (
                            <div key={i} className="drift-summary-change">
                                <DriftStatusBadge status={change.status} size="small" showLabel={false} />
                                <span className="drift-summary-change__asset">{change.assetValue}</span>
                                <span className="drift-summary-change__summary">{change.summary}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default DriftSummaryCard;
